import {
  Typography,
  Paper,
  Toolbar,
  Grid,
  makeStyles,
} from '@material-ui/core';
import * as React from 'react';
import { Report, ReportFilter } from 'interfaces';
import { DelayedLinearProgress, SortableTable } from 'components';
import { SortableTableHeader } from 'components/SortableTable/components';
import { useRouteMatch } from 'react-router-dom';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { ReportService } from 'services';
import { KeyboardDatePicker } from '@material-ui/pickers';

/**
 * Renders ANY report defined in the database.
 *
 * The two reports that existed before this each had their own component with
 * their own hardcoded column list, so a report added to the database appeared
 * in the list and then bounced straight back to it when opened. Columns are
 * taken from whatever the query actually returns, so a new report needs no
 * frontend work at all.
 */
const useStyles = makeStyles(() => ({
  filterElement: {
    width: '75%',
    padding: '1em 2em 1em 2em',
  },
  filter: {
    paddingBottom: '5px',
  },
}));

/**
 * Title -> URL. Strips anything that is not a letter or a digit, so a title
 * containing brackets cannot produce a URL that fails to match itself:
 * "Fiscal Journal (money events)" used to become
 * "fiscal-journal-(money-events)" and the report could not be opened.
 * The list and this page MUST use the same rule - that is why it is written
 * out identically in both and not quietly changed in one.
 */
export const slugifyReportTitle = (title: string) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const slugify = slugifyReportTitle;

const labelFor = (key: string) =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const GenericReport: React.FC = () => {
  const match = useRouteMatch<{ slug?: string }>();
  const classes = useStyles();

  const [loading, setLoading] = React.useState(true);
  const [report, setReport] = React.useState<Report | null>(null);
  const [rows, setRows] = React.useState<Record<string, any>[]>([]);
  const [dateFrom, setDateFrom] = React.useState<Date | null>();
  const [dateTo, setDateTo] = React.useState<Date | null>();

  // The URL carries the slugified title, so resolve it back to the report.
  React.useEffect(() => {
    const wanted = (match.params as any).slug as string | undefined;
    ReportService.getAllReports().then((all) => {
      const found = all.find((r) => slugify(r.title) === wanted) ?? null;
      setReport(found);
      if (!found) {
        setLoading(false);
      }
    });
  }, [match.params]);

  React.useEffect(() => {
    if (!report) {
      return;
    }
    setLoading(true);
    const filter: ReportFilter = {
      dateFrom: dateFrom?.toDateString() ?? undefined,
      dateTo: dateTo?.toDateString() ?? undefined,
    };
    ReportService.getReportData(report.id, filter)
      .then((response) => {
        setRows(Array.isArray(response) ? response : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [report, dateFrom, dateTo]);

  const keys = rows.length > 0 ? Object.keys(rows[0]) : [];
  const columns: SortableTableHeader[] = keys.map((key) => ({
    key,
    label: labelFor(key),
  }));

  const tableRows = rows.map((row, index) => ({
    key: String(index),
    cells: keys.map((key) => ({
      key,
      display: row[key] === null ? '' : String(row[key]),
      sortValue: row[key],
    })),
  }));

  return (
    <React.Fragment>
      <BreadcrumbsItem to={match.url}>
        {report ? report.title : 'Report'}
      </BreadcrumbsItem>

      <DelayedLinearProgress loading={loading} />
      <Paper>
        <Toolbar style={{ justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {report ? report.title : 'Report not found'}
          </Typography>
        </Toolbar>

        <Grid container className={classes.filter}>
          <Grid item xs={12} md={4} className={classes.filterElement}>
            <KeyboardDatePicker
              margin="normal"
              label="From"
              format="dd/MM/yyyy"
              value={dateFrom ?? null}
              onChange={setDateFrom}
              clearable
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4} className={classes.filterElement}>
            <KeyboardDatePicker
              margin="normal"
              label="To"
              format="dd/MM/yyyy"
              value={dateTo ?? null}
              onChange={setDateTo}
              clearable
              fullWidth
            />
          </Grid>
        </Grid>

        <SortableTable
          columns={columns}
          rows={tableRows}
          loading={loading}
          emptyTableText="Nothing recorded for these dates yet."
        />
      </Paper>
    </React.Fragment>
  );
};
